import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setupApi, eventApi, auditApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import StatusBadge from "@/components/StatusBadge";
import { SETUP } from "@/constants/testIds";
import { Settings2, Calendar, Activity, CheckCircle2, AlertTriangle, ArrowRight, Zap } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const brand = useBrand();
  const { user } = useAuth();
  const [readiness, setReadiness] = useState(null);
  const [events, setEvents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      setupApi.getReadiness(),
      eventApi.listEvents(),
      auditApi.listAudit({ limit: 5 }),
    ]).then(([r, e, a]) => {
      setReadiness(r.data);
      setEvents(e.data.slice(0, 5));
      setAudit(a.data.results || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;

  const active = readiness?.active_domains || 0;
  const total = readiness?.total_domains || 7;
  const pct = readiness?.overall_percentage || 0;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#181818] font-sans">Platform Dashboard</h1>
          <p className="text-sm text-[#706E6B] mt-1">{brand.productName} — Stage 00 ONE Core</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-[#706E6B]">{user?.tenant_name || user?.tenant_id}{user?.tenant_number ? ` · ${user.tenant_number}` : ""}</span>
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Setup Readiness", value: `${pct}%`, icon: Zap, color: "text-[#0176D3]", bg: "bg-[#EEF4FF]" },
          { label: "Active Domains", value: `${active}/${total}`, icon: CheckCircle2, color: "text-[#2E844A]", bg: "bg-emerald-400/10" },
          { label: "Total Events", value: events.length, icon: Calendar, color: "text-indigo-400", bg: "bg-indigo-400/10" },
          { label: "Audit Events", value: audit.length, icon: Activity, color: "text-[#8C4B00]", bg: "bg-amber-400/10" },
        ].map(stat => (
          <div key={stat.label} className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4 card-hover">
            <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center mb-3`}>
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
            </div>
            <p className="text-2xl font-bold text-[#181818] font-sans">{stat.value}</p>
            <p className="text-xs text-[#706E6B] mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Setup Studio Overview */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-[#0176D3]" />
            <h2 className="text-sm font-semibold text-[#181818] font-sans">Setup Studio Overview</h2>
          </div>
          <button onClick={() => navigate("/setup-studio")} className="flex items-center gap-1 text-xs text-[#0176D3] hover:text-sky-300 transition-colors" data-testid={SETUP.overallReadiness}>
            View All <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        {/* Overall readiness bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-[#706E6B] mb-1.5">
            <span className="font-mono">Overall Readiness</span>
            <span className="font-bold text-[#0176D3]" data-testid={SETUP.overallReadiness}>{pct}%</span>
          </div>
          <div className="h-2 bg-[#F3F3F3] rounded-full overflow-hidden">
            <div className="h-full bg-[#0176D3] rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Domain pills */}
        <div className="flex flex-wrap gap-2">
          {(readiness?.domains || []).map(d => (
            <button key={d.code} onClick={() => navigate(`/setup-studio/${d.code}`)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] hover:border-[#0176D3]/40 transition-colors">
              <span className="text-xs text-[#444444] font-sans">{d.name}</span>
              <StatusBadge status={d.status} showDot={true} />
            </button>
          ))}
        </div>
      </div>

      {/* Events + Audit Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Events */}
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-400" />
              <h2 className="text-sm font-semibold text-[#181818] font-sans">Recent Events</h2>
            </div>
            <button onClick={() => navigate("/events")} className="text-xs text-[#0176D3] hover:text-sky-300">View All</button>
          </div>
          {events.length === 0 ? (
            <p className="text-sm text-[#706E6B] text-center py-6">No events yet</p>
          ) : (
            <div className="space-y-2">
              {events.map(ev => (
                <div key={ev.id} className="flex items-center justify-between p-2.5 rounded-lg bg-[#F3F3F3] border border-[#EEF4FF]">
                  <div>
                    <p className="text-sm text-[#181818] font-semibold">{ev.name}</p>
                    <p className="text-xs text-[#706E6B] font-mono">{ev.event_number} · {ev.event_type_name}</p>
                  </div>
                  <StatusBadge status={ev.status === "PLANNING" ? "IN_PROGRESS" : ev.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Audit */}
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#8C4B00]" />
              <h2 className="text-sm font-semibold text-[#181818] font-sans">Recent Activity</h2>
            </div>
            <button onClick={() => navigate("/administration")} className="text-xs text-[#0176D3] hover:text-sky-300">View Audit</button>
          </div>
          {audit.length === 0 ? (
            <p className="text-sm text-[#706E6B] text-center py-6">No activity</p>
          ) : (
            <div className="space-y-2">
              {audit.map(ev => (
                <div key={ev.id} className="p-2.5 rounded-lg bg-[#F3F3F3] border border-[#EEF4FF]">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-[#0176D3]">{ev.action}</span>
                    <span className="text-[10px] font-mono text-[#706E6B]">{ev.entity_type}</span>
                  </div>
                  <p className="text-xs text-[#706E6B] mt-0.5 truncate">{ev.summary}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto animate-pulse">
      <div className="h-8 bg-[#F3F3F3] rounded-lg w-64" />
      <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-[#F3F3F3] rounded-xl" />)}</div>
      <div className="h-48 bg-[#F3F3F3] rounded-xl" />
    </div>
  );
}
